/**
 * The attribute names `r-button` reads.
 *
 * Every one is button-specific, including the label and the icon, which are
 * conceptually the same thing `r-text` and `r-icon` already offer. Sharing
 * their attribute names would have saved two lines of editor wiring and cost
 * the documentation contract: RuleCMS publishes one page per component-scope
 * attribute and requires exactly one owning component, so a shared name has
 * no single page to link to from the drawer.
 *
 * Reuse happens a level down instead. The composer resolves an attribute to
 * an *editor* by name, and several names already share one editor, so these
 * point at the existing text field and the existing Lucide picker without
 * either component having to give up ownership of its own settings.
 *
 * Nothing here duplicates a column style attribute. Colour, border, radius,
 * spacing and width already have editors that write into the same bag, and a
 * second way to set the same CSS property would only create a question about
 * which one wins.
 */
export const BUTTON_PRESET_ATTRIBUTE = 'button-preset-3-resolutions';
export const BUTTON_LABEL_ATTRIBUTE = 'button-label-3-resolutions';
export const BUTTON_LINK_ATTRIBUTE = 'button-link-3-resolutions';
export const BUTTON_DISABLED_ATTRIBUTE = 'button-disabled-3-resolutions';
export const BUTTON_ICON_ATTRIBUTE = 'button-icon-3-resolutions';
export const BUTTON_ICON_POSITION_ATTRIBUTE = 'button-icon-position-3-resolutions';
export const BUTTON_ARIA_LABEL_ATTRIBUTE = 'aria-label';

/** What a freshly dropped button says before the author types anything. */
export const DEFAULT_BUTTON_LABEL = 'Button';

export const ICON_POSITIONS = ['leading', 'trailing'] as const;
export type IconPosition = (typeof ICON_POSITIONS)[number];

export const DEFAULT_ICON_POSITION: IconPosition = 'leading';

type Attributes = { [key: string]: any } | undefined;

/**
 * An unset label falls back to the placeholder, but a label the author
 * deliberately cleared stays cleared — that is how an icon-only button is
 * made, so an empty string cannot be treated as "nothing set".
 */
export const resolveButtonLabel = (componentAttributes: Attributes): string => {
  const value = componentAttributes?.[BUTTON_LABEL_ATTRIBUTE];
  if (value === undefined || value === null) {
    return DEFAULT_BUTTON_LABEL;
  }
  return typeof value === 'string' ? value : String(value);
};

/**
 * Select editors in the composer store `'true'` / `'false'` as strings, while
 * a hand-written or migrated config may carry a real boolean. Both mean the
 * same thing here.
 */
export const resolveButtonDisabled = (componentAttributes: Attributes): boolean => {
  const value = componentAttributes?.[BUTTON_DISABLED_ATTRIBUTE];
  if (typeof value === 'boolean') {
    return value;
  }
  return typeof value === 'string' && value.trim().toLowerCase() === 'true';
};

export const resolveIconPosition = (componentAttributes: Attributes): IconPosition => {
  const value = componentAttributes?.[BUTTON_ICON_POSITION_ATTRIBUTE];
  return value === 'trailing' ? 'trailing' : DEFAULT_ICON_POSITION;
};

export const resolveButtonAriaLabel = (componentAttributes: Attributes): string | undefined => {
  const value = componentAttributes?.[BUTTON_ARIA_LABEL_ATTRIBUTE];
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
};
